<script setup>
import {
    Scene,
    AmbientLight,
    PerspectiveCamera,
    WebGLRenderer,
    ACESFilmicToneMapping,
    EquirectangularReflectionMapping,
    MathUtils, Vector3,
} from "three";
import {onMounted, ref, computed, onUnmounted} from "vue";
import {RGBELoader} from "three/examples/jsm/loaders/RGBELoader";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import TWEEN from '@tweenjs/tween.js'

const container = ref(null);
let width = window.innerWidth;
let height = window.innerHeight;
const aspectRatio = computed(() => {
    return width / height;
});
let camera;
let renderer;
let scene;
let controls, orbit;
let object;
let tween;

const edges = [
    [-0.8812722044129341, 0.87641037806038, -1.0059772382700223],
    [1.9897341801254675, 0.2769760885011374, 0.5570443931370856],
    [0.034399184835486527, 0.12004774888174094, -0.8191180530709294],
    [1.0226055893811468, 0.6774036287025645, 1.9008959555849192],
    [2.411585101855935, 0.32325338578227253, 2.905657890860005],
    [-0.7133197662125124, -0.330223277711733, -2.898385310912861],
    [1.0048898379663302, 1.0751102457688333, -1.8554966034451168],
    [-2.091682987491257, -1.0848297217967733, 1.8875041249567703],
    [0.09064661562643572, -1.3250762118301058, 0.9109461501261081],
    [2.1143135599606477, -0.6867054715292583, -0.28125828821097176],
    [-2.107884291590018, -0.6716871301651055, -1.8677597427066575],
    [-3.110428912477269, -0.13974565508852274, 0.8242387770701024],
    [2.591173045492335, -0.6423546737355664, -2.523992498005876],
    [-0.5121258484534675, 0.6002478647754543, 2.5195258748692],
    [2.331610928996781, 0.5356181537368883, -2.1204265031427103],
    [-0.8937524593214238, -0.5291977906950194, 2.0815670885054196],
    [-0.9999554205720474, 0.6549737999000378, 0.2928971575187608],
    [-3.095088037092178, 1.2986186877210413, -0.8653768191898954],
    [-1.1247875800014178, -0.29077814026368176, -0.5311880530049953],
    [0.7113310641748114, 0.6990281296165708, 0.8547272561606154],
];

const emit = defineEmits(['roll']);

const init = () => {
    camera = new PerspectiveCamera(45, width/height, 0.25, 20);
    camera.position.set(-1.8, 0.6, 2.7);
    camera.lookAt(new Vector3(0,0,0));

    scene = new Scene();

    scene.add(new AmbientLight(0x404040, 10));

    new RGBELoader()
        .setPath('/models/')
        .load('royal_esplanade_1k.hdr', function (texture) {

            texture.mapping = EquirectangularReflectionMapping;

            // scene.background = texture;
            scene.environment = texture;

            const loader = new GLTFLoader().setPath('/models/');
            loader.load('d20_1k.glb', async function (gltf) {
                object = gltf.scene;
                await renderer.compileAsync(object, camera, scene);
                scene.add(object);
                requestAnimationFrame(animate)
            });
        });

    renderer = new WebGLRenderer({antialias: true, alpha: true});
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    container.value.appendChild(renderer.domElement);
}

const animate = (time) => {
    if (tween) {
        tween.update(time)
    }

    renderer.render(scene, camera);
    requestAnimationFrame(animate)
}

const rotate = (x, y, z) => {
    object.rotation.setFromVector3(new Vector3(MathUtils.degToRad(x), MathUtils.degToRad(y), MathUtils.degToRad(z)));
}

const resize = () => {
    width = container.value.getBoundingClientRect().width;
    height = container.value.getBoundingClientRect().height;
    camera.aspect = width/height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
};


const rotationAnimation = (edge, coords) => {
    tween = new TWEEN.Tween(object.rotation)
        .to({
                x: coords[0],
                y: coords[1] + randomInt(3, 6) * 2 * Math.PI,
                z: coords[2]
            },
            3000
        ) // relative animation
        .easing(TWEEN.Easing.Quartic.InOut)
        .onComplete(() => {
            emit('roll', edge);
        })
        .start();
}

const randomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

const onClick = () => {
    const edge = randomInt(1,20);
    rotationAnimation(edge, edges[edge - 1]);
}

onMounted(() => {
    init();
    resize();
    window.addEventListener("resize", resize);
});

onUnmounted(() => {
    window.removeEventListener("resize", resize);
});

defineExpose({
    onClick
});
</script>

<template>
    <div ref="container" class="w-full h-full 12"></div>
</template>
